/**
 * CommandBus — Thin MCP adapter.
 *
 * Registers handlers both locally (for synchronous dispatch backward compat)
 * and on mcpBus (for the MCP middleware chain). dispatch() calls the local
 * handler synchronously, preserving the existing contract that callers
 * (chat-model-adapter, chat-pipeline-wiring) can see side effects immediately
 * after dispatch() returns.
 *
 * External callers (studio-bridge, agents) route through mcpBus.execute()
 * directly, getting the full middleware chain.
 */

import type { CliAction } from '@/store/chat-types'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { STUDIO_NS } from '@/mcp/types'

type CommandHandler = (payload: Record<string, unknown>) => void

export class CommandBus {
  /** Local handler map for synchronous dispatch. */
  private handlers = new Map<string, CommandHandler>()

  /**
   * Register a handler both locally (sync dispatch) and on mcpBus (MCP path).
   * @deprecated For new tools, use mcpBus.registerTool() directly.
   */
  register(type: string, handler: CommandHandler): void {
    this.handlers.set(type, handler)
    mcpBus.registerTool(type, {
      description: `Studio command: ${type}`,
      inputSchema: { type: 'object' },
      handler: (params) => handler(params),
    })
  }

  /**
   * @deprecated Kept for backward compat.
   */
  unregister(type: string): void {
    this.handlers.delete(type)
    console.warn(`[CommandBus] unregister('${type}') — use mcpBus directly`)
  }

  /**
   * Dispatch an action synchronously via the local handler.
   * The same handler is also available on mcpBus for external callers.
   */
  dispatch(action: CliAction): boolean {
    const handler = this.handlers.get(action.type)
    if (!handler) return false
    handler(action.payload)
    return true
  }

  dispatchAll(actions: CliAction[]): void {
    for (const action of actions) {
      this.dispatch(action)
    }
  }

  registeredTypes(): string[] {
    return mcpBus.getManifest().map((t) => t.name.replace(STUDIO_NS, ''))
  }
}

/** Singleton instance for backward-compatible imports. */
export const commandBus = new CommandBus()
